import moment from 'moment'
import { searchTrips } from '@/api/modules/trip'
import { calculatePrice as calculateOrange } from '@/api/modules/products/orangetickets'
import { calculatePrice as calculateSeat } from '@/api/modules/products/seattickets'
import { calculatePrice as calculateDSBAll } from '@/api/modules/products/dsballtickets'
import { calculatePrice as calculateTime } from '@/api/modules/products/timetickets'
import { makeUtcDate } from '@/utilities/time'
import get from 'lodash/get'

// weights for sorting
const weights = {
	dsbstandardticket_standard: 10,
	dsbstandardticket_dsb1: 20,
	dsborangeticket_orange: 60,
	dsborangeticket_orangefri: 70,
	orangeticket_refundable: 40,
	orangeticket_nonrefundable: 30,
	timeticket: 50,
	dsbseatticket: 80,
	seatticket: 90
}

function ensureUtc(trip) {
	return {
		...trip,
		instant: makeUtcDate(trip.instant)
	}
}

async function fetchSeatTicket(context, tripId) {
	if (!context.rootGetters['products/seatticket/isEnabled']) return {}
	const response = await calculateSeat(tripId)
	if (!response.ok || !response.responseBody) return {}
	return response.responseBody
}

async function fetchAllDSBTicketProducts(context, tripId, passengers, seatticket) {
	const response = await calculateDSBAll(tripId, passengers)
	let hasDsbTicketsRequestFailed = false

	if (!response) {
		hasDsbTicketsRequestFailed = true

		return {
			hasDsbTicketsRequestFailed: hasDsbTicketsRequestFailed
		}
	}

	if (!response.ok || response.responseBody === '') {
		hasDsbTicketsRequestFailed = true
	}

	const dsbStandardProductVariants = response.responseBody.dsbStandardProductVariants
	const dsbOrangeProductVariants = response.responseBody.dsbOrangeProductVariants
	const dsbSeatProducts = response.responseBody.dsbSeatProducts

	const passengerCount = passengers.reduce((acc, p) => acc + p.passengerTypeCount, 0)
	const dsbStandardProducts = dsbStandardProductVariants
		? composeDSBStandardTicketProducts(context, passengerCount, seatticket, dsbStandardProductVariants)
		: {}
	const dsbOrangeProducts = dsbOrangeProductVariants
		? composeDSBOrangeTicketProducts(context, passengerCount, seatticket, dsbOrangeProductVariants)
		: {}

	return {
		dsbseatticket: dsbSeatProducts,
		dsbstandardtickets: dsbStandardProducts,
		dsbOrangeProducts: dsbOrangeProducts,
		hasDsbTicketsRequestFailed: hasDsbTicketsRequestFailed
	}
}

function composeDSBStandardTicketProducts(context, passengerCount, seatticket, variants) {
	const levels = {}
	levels['standard'] = variants.filter(v => v.serviceLevel === 'Standard')
	levels['dsb1'] = variants.filter(v => v.serviceLevel === 'DSB1')

	const products = {}
	Object.keys(levels).forEach(level => {
		if (levels[level].length) {
			const variantWithoutSeating = levels[level].find(v => !v.seating)
			const variantWithSeating = levels[level].find(v => v.seating)

			if (variantWithoutSeating) {
				let seatTypes = []
				let seatLegs = []

				if (variantWithSeating) {
					seatLegs = variantWithSeating.seating.seatLegs
					const priceOre = (variantWithSeating.priceOre - variantWithoutSeating.priceOre) / passengerCount
					variantWithSeating.seating.seatTypeIds.forEach(id => {
						seatTypes.push({
							productId: variantWithSeating.productVariantId,
							priceOre,
							available: Infinity,
							...context.rootGetters['seats/getTypeById'](id)
						})
					})
				} else {
					seatLegs = get(seatticket, 'seatLegs', [])
					get(seatticket, 'products', []).forEach(product => {
						seatTypes.push({
							productId: product.productId,
							priceOre: product.nonRefundablePriceOre,
							available: product.seatsAvailableCount,
							...context.rootGetters['seats/getTypeById'](product.seatTypeId)
						})
					})
				}

				seatTypes = seatTypes.filter(t => t.id)

				products[`dsbstandardticket_${level}`] = {
					productType: context.rootState.products.dsbstandardticket.info.identifier,
					serviceLevel: level,
					variantWithoutSeating,
					variantWithSeating,
					seatTypes,
					seatLegs,
					calculatedPriceOre: variantWithoutSeating.priceOre
				}
			}
		}
	})

	return products
}

function composeDSBOrangeTicketProducts(context, passengerCount, seatticket, variants) {
	const levels = {}
	levels['orange'] = variants.filter(v => v.orangeTicketType === 'Orange')
	levels['orangefri'] = variants.filter(v => v.orangeTicketType === 'OrangeFri')

	const products = {}
	Object.keys(levels).forEach(level => {
		if (levels[level].length) {
			const variantWithoutSeating = levels[level].find(v => !v.seating)
			const variantWithSeating = levels[level].find(v => v.seating)

			if (variantWithoutSeating) {
				let seatTypes = []
				let seatLegs = []
				let tripLegs = null

				if (variantWithSeating) {
					seatLegs = variantWithSeating.seating.seatLegs
					if (!variantWithSeating.isTicketValidForWholeTrip) {
						tripLegs = variantWithSeating.validLegs
					}
					const priceOre = (variantWithSeating.priceOre - variantWithoutSeating.priceOre) / passengerCount
					variantWithSeating.seating.seatTypeIds.forEach(id => {
						seatTypes.push({
							productId: variantWithSeating.productVariantId,
							priceOre,
							available: Infinity,
							...context.rootGetters['seats/getTypeById'](id)
						})
					})
				} else {
					seatLegs = get(seatticket, 'seatLegs', [])
					if (!variantWithoutSeating.isTicketValidForWholeTrip) {
						tripLegs = variantWithoutSeating.validLegs
					}
					tripLegs = variantWithoutSeating.validLegs
					get(seatticket, 'products', []).forEach(product => {
						seatTypes.push({
							productId: product.productId,
							priceOre: product.nonRefundablePriceOre,
							available: product.seatsAvailableCount,
							...context.rootGetters['seats/getTypeById'](product.seatTypeId)
						})
					})
				}

				seatTypes = seatTypes.filter(t => t.id)

				products[`dsborangeticket_${level}`] = {
					productType: context.rootState.products.dsborangeticket.info.identifier,
					orangeTicketType: level,
					variantWithoutSeating,
					variantWithSeating,
					seatTypes,
					seatLegs,
					tripLegs,
					calculatedPriceOre: variantWithoutSeating.priceOre
				}
			}
		}
	})

	return products
}

async function fetchOrangeTicketProduct(context, tripId, passengers, seatticket) {
	if (!context.rootGetters['products/orangeticket/isEnabled']) return {}
	const response = await calculateOrange(tripId, passengers)
	if (!response.ok) return {}
	const product = response.responseBody.product
	if (!product) return {}

	const products = {}
	const seatLegs = get(seatticket, 'seatLegs', [])
	const seatTypes = get(seatticket, 'products', [])
		.map(product => ({
			productId: product.productId,
			priceOre: product.nonRefundablePriceOre,
			available: product.seatsAvailableCount,
			...context.rootGetters['seats/getTypeById'](product.seatTypeId)
		}))
		.filter(t => t.id)

	if (product.refundablePriceOre) {
		products['orangeticket_refundable'] = {
			productType: context.rootState.products.orangeticket.info.identifier,
			product,
			refundable: true,
			seatTypes,
			seatLegs,
			calculatedPriceOre: product.refundablePriceOre
		}
	}
	if (product.nonRefundablePriceOre) {
		products['orangeticket_nonrefundable'] = {
			productType: context.rootState.products.orangeticket.info.identifier,
			product,
			refundable: false,
			seatTypes,
			seatLegs,
			calculatedPriceOre: product.nonRefundablePriceOre
		}
	}

	return products
}

async function fetchTimeTicketProduct(context, departureInstant, passengers) {
	if (!context.rootGetters['products/timeticket/isEnabled']) return {}
	const response = await calculateTime(departureInstant, passengers)
	if (!response.ok || !response.responseBody.priceOre) return {}
	return {
		timeticket: {
			productType: context.rootState.products.timeticket.info.identifier,
			departureInstant,
			...response.responseBody,
			calculatedPriceOre: response.responseBody.priceOre
		}
	}
}

function seatOnlyTicketProduct(context, dsbseatticket, seatticket) {
	if (dsbseatticket && dsbseatticket.seating) {
		const seatTypes = []
		dsbseatticket.seating.productVariants.forEach(product => {
			product.availableSeatTypeIds.forEach(id => {
				seatTypes.push({
					productId: product.productVariantId,
					priceOre: dsbseatticket.seating.priceOre,
					serviceLevel: product.serviceLevel.toLowerCase(),
					available: Infinity,
					...context.rootGetters['seats/getTypeById'](id)
				})
			})
		})
		return {
			dsbseatticket: {
				productType: context.rootState.products.dsbseatticket.info.identifier,
				seatingOnly: true,
				seatLegs: dsbseatticket.seating.seatLegs,
				seatTypes,
				calculatedPriceOre: dsbseatticket.seating.priceOre
			}
		}
	}

	if (seatticket && seatticket.products && seatticket.products.length) {
		const seatTypes = seatticket.products
			.map(product => ({
				productId: product.productId,
				priceOre: product.nonRefundablePriceOre,
				available: product.seatsAvailableCount,
				...context.rootGetters['seats/getTypeById'](product.seatTypeId)
			}))
			.filter(t => t.id)
		const calculatedPriceOre = seatTypes[0].priceOre * context.rootGetters['passenger/getPassengerCount']
		return {
			seatticket: {
				productType: context.rootState.products.seatticket.info.identifier,
				seatingOnly: true,
				seatLegs: seatticket.seatLegs,
				seatTypes,
				calculatedPriceOre
			}
		}
	}

	return {}
}

async function fetchAndAddTripProducts(context, trip, passengers) {
	const [seatticket] = await Promise.all([fetchSeatTicket(context, trip.tripId)])

	const [orangeticket, timeticket] = await Promise.all([
		fetchOrangeTicketProduct(context, trip.tripId, passengers, seatticket),
		fetchTimeTicketProduct(context, trip.departureInstant, passengers)
	])

	const { dsbseatticket, dsbstandardtickets, dsbOrangeProducts, hasDsbTicketsRequestFailed } = await fetchAllDSBTicketProducts(
		context,
		trip.tripId,
		passengers,
		seatticket
	)

	const seatonly = seatOnlyTicketProduct(context, dsbseatticket, seatticket)

	const cheapestDsbStandardTicket = Object.values(dsbstandardtickets)
		.map(p => p.calculatedPriceOre)
		.sort((a, b) => a - b)[0]

	const isArrivaOnly = !trip.legs.map(part => ['ra', 'rx'].includes(part.transportName.substr(0, 2).toLowerCase())).includes(false)

	let products = { ...dsbstandardtickets, ...dsbOrangeProducts }

	if (isArrivaOnly) {
		products = { ...products, ...timeticket }

		if (!cheapestDsbStandardTicket) {
			Object.keys(orangeticket).forEach(key => {
				products[key] = {
					...orangeticket[key],
					seatingProducts: seatticket.products
				}
			})
		} else {
			Object.keys(orangeticket).forEach(key => {
				if (orangeticket[key].calculatedPriceOre < cheapestDsbStandardTicket) {
					products[key] = {
						...orangeticket[key],
						seatingProducts: seatticket.products
					}
				}
			})
		}
	}

	Object.keys(products).forEach(key => {
		products[key].weight = weights[key] || 10000
	})

	const prices = Object.values(products)
		.filter(p => !p.seatingOnly)
		.map(p => p.calculatedPriceOre)
	prices.sort((a, b) => a - b)
	const cheapestPrice = prices[0]

	products = { ...products, ...seatonly }

	return {
		...trip,
		cheapestPrice,
		products,
		hasDsbTicketsRequestFailed
	}
}

async function addSeatingToLegs(trip) {
	const hasSeating = ({ arrival, departure }) => {
		if (!arrival.stopId || !departure.stopId) return false

		return Boolean(
			Object.values(trip.products).find(p => {
				return p.seatLegs && p.seatLegs.find(l => l.startStopId === departure.stopId && l.endStopId === arrival.stopId)
			})
		)
	}

	const legs = trip.legs.map(leg => {
		return { ...leg, seating: hasSeating(leg) }
	})

	return {
		...trip,
		legs
	}
}

export const state = {
	trips: [],
	loading: false,
	trip: null,
	productKey: null,
	seatTypeId: null,
	seatQuantity: 0
}

export const mutations = {
	setTrips: (state, trips) => {
		state.trips = trips
		// state.trip = null
		state.productKey = null
		state.seatTypeId = null
		state.seatQuantity = 0
	},
	setLoading: (state, loading) => {
		state.loading = loading
	},
	setTrip: (state, trip) => {
		const filterTrips = state.trips.filter(t => t.tripId === trip.tripId)
		state.trip = filterTrips[0]
		state.productKey = null
		state.seatTypeId = null
		state.seatQuantity = 0
	},
	setProductKey: (state, productKey) => {
		state.productKey = productKey
		state.seatTypeId = null
	},
	setSeatTypeId: (state, seatTypeId) => {
		state.seatTypeId = seatTypeId
	},
	setSeatQuantity: (state, seatQuantity) => {
		state.seatQuantity = seatQuantity
	},
	updateTrip: (state, payload) => {
		var tripIndex = state.trips.findIndex(x => x.tripId == payload.oldTrip.tripId)
		state.trips[tripIndex] = payload.newTrip
		state.trip = payload.newTrip
		state.productKey = null
		state.seatTypeId = null
		state.seatQuantity = 0
	}
}

export const getters = {
	getTrips: state => state.trips,
	getTrip: state => state.trip,
	isLoading: state => state.loading,
	getProductKey: state => state.productKey,
	getSeatTypeId: state => state.seatTypeId,
	getSeatType: (_state, getters) => (get(getters, 'getSeatTypes') || []).find(t => t.id === getters.getSeatTypeId),
	getSeatQuantity: state => state.seatQuantity,
	getSeatLegs: (_state, getters) => get(getters, 'getProduct.seatLegs', null),
	getSeatTypes: (_state, getters) => get(getters, 'getProduct.seatTypes', null),
	getMinimumSeatQuantity: (_state, getters, _rootState, rootGetters) => {
		if (getters.isSeatRequired) {
			return rootGetters['passenger/getPassengerCount']
		} else {
			return 0
		}
	},
	isSeatRequired: (_state, getters) => {
		return !!get(getters, 'getTrip.legs', []).find(l => l.seatRequired === true)
	},
	getProduct: state => (state.productKey && state.trip ? get(state, `trip.products.${state.productKey}`, null) : null),
	isProductValid: (_state, getters, _rootState, rootGetters) => {
		const passengerCount = rootGetters['passenger/getPassengerCount']
		if (!getters.getProduct) return false
		if (getters.isSeatRequired && getters.getSeatQuantity < passengerCount) return false
		if (getters.getSeatQuantity > 0 && !getters.getSeatType) return false
		if (getters.getProduct.seatingOnly && getters.getSeatQuantity === 0) return false
		return true
	},
	getFromStation: (_state, { getTrip, getProduct }) => {
		if (!getTrip || !getTrip.legs || !getTrip.legs.length) return null
		if (getProduct && getProduct.tripLegs && getProduct.tripLegs.length) {
			const leg = getTrip.legs.find(l => l.departure.stopId === getProduct.tripLegs[0].startStopId)
			if (leg) {
				return leg.departure.locationDescription
			}
		}
		return getTrip.legs[0].departure.locationDescription
	},
	getToStation: (_state, { getTrip, getProduct }) => {
		if (!getTrip || !getTrip.legs || !getTrip.legs.length) return null
		if (getProduct && getProduct.tripLegs) {
			const leg = getTrip.legs.find(l => l.arrival.stopId === getProduct.tripLegs[getProduct.tripLegs.length - 1].endStopId)
			if (leg) {
				return leg.arrival.locationDescription
			}
		}
		return getTrip.legs[getTrip.legs.length - 1].arrival.locationDescription
	}
}

const prepareOrder = ({ getters, dispatch }) => {
	if (!getters.getTrip) return null
	if (!getters.getProduct) return null

	const { tripId, departureInstant, arrivalInstant, departureDate } = getters.getTrip
	const { productType, ...product } = getters.getProduct
	const seatType = getters.getSeatType
	const seatQuantity = getters.getSeatQuantity

	let calculatedPriceOre = product.calculatedPriceOre
	if (seatQuantity && seatType) {
		calculatedPriceOre = calculatedPriceOre + seatQuantity * seatType.priceOre
		if (product.seatingOnly) {
			calculatedPriceOre = seatQuantity * seatType.priceOre
		}
	}

	return dispatch(
		'order/prepare',
		{
			...product,
			productType,
			trip: { tripId, departureInstant, arrivalInstant, departureDate },
			seatType,
			seatQuantity,
			calculatedPriceOre
		},
		{ root: true }
	)
}

export const actions = {
	selectTrip: (context, trip) => {
		context.commit('setTrip', trip)
	},
	selectProduct: (context, productKey) => {
		const trip = context.getters.getTrip
		if (!trip) return null
		const product = context.getters.getTrip.products[productKey]
		if (!product) return null
		const passengerCount = context.rootGetters['passenger/getPassengerCount']
		context.commit('setProductKey', productKey)
		if (product.seatingOnly) {
			if (context.getters.getSeatQuantity === 0) {
				context.commit('setSeatQuantity', passengerCount)
			}
			context.commit('setSeatTypeId', product.seatTypes[0].id)
		} else {
			if (context.getters.isSeatRequired && context.getters.getSeatQuantity < passengerCount) {
				context.commit('setSeatQuantity', passengerCount)
			}
			if (context.getters.getSeatQuantity > 0) {
				context.commit('setSeatTypeId', product.seatTypes[0].id)
			}
		}
		return prepareOrder(context)
	},
	selectSeatType: (context, seatTypeId) => {
		if (!context.getters.getSeatTypes) return null
		const seatType = context.getters.getSeatTypes.find(t => t.id === seatTypeId)
		if (!seatType) return null
		context.commit('setSeatTypeId', seatTypeId)
		return prepareOrder(context)
	},
	selectSeatQuantity: (context, seatQuantity) => {
		const prev = context.getters.getSeatQuantity
		const product = context.getters.getProduct
		const seatType = context.getters.getSeatTypeId
		context.commit('setSeatQuantity', seatQuantity)
		if (prev === 0 && seatQuantity > 0 && product && !seatType) {
			context.commit('setSeatTypeId', product.seatTypes[0].id)
		}
		return prepareOrder(context)
	},
	search: (context, payload) => {
		if (context.state.loading) return null

		const prepend = payload && payload.prepend
		const append = payload && payload.append
		const trip = payload && payload.trip

		if (prepend && append) throw new Error('cannot prepend and append at the same time')

		const tripType = 'Normal'

		let searchType = context.rootGetters['date/getSelectedSearchType']
		let instant = context.rootGetters['date/getDepartureDateAsMoment']

		if (prepend) {
			searchType = 'Arrivals'
			const first = context.state.trips[0]
			if (first) {
				instant = moment(first.arrivalInstant).subtract(1, 'minute')
				if (instant.isBefore(moment())) instant = moment()
			}
		}

		if (append) {
			searchType = 'Departures'
			const last = context.state.trips[context.state.trips.length - 1]
			if (last) {
				instant = moment(last.departureInstant).add(1, 'minute')
				if (instant.isBefore(moment())) instant = moment()
			}
		}

		if (instant) instant = instant.toJSON()

		const startLocation = context.rootGetters['location/getFromLocation']
		const endLocation = context.rootGetters['location/getToLocation']
		const passengers = context.rootGetters['passenger/getPassengers'].map(p => {
			return {
				passengerTypeId: p.passengerType.id,
				passengerTypeCount: p.count
			}
		})

		if (!instant) throw new Error('instant not provided, cannot search trips')
		if (!startLocation || !endLocation) throw new Error('from or to location is missing, cannot search trips')
		if (!passengers.length) throw new Error('no passengers, cannot search trips')

		context.commit('setLoading', true)

		if (trip) {
			return Promise.resolve(fetchAndAddTripProducts(context, trip, passengers))
				.then(newTrip => {
					context.commit('updateTrip', { oldTrip: trip, newTrip })
				})
				.finally(() => {
					context.commit('setLoading', false)
				})
		} else {
			return searchTrips(
				tripType,
				{
					stopId: startLocation.id,
					locationName: startLocation.name,
					coordinates: startLocation.coordinates
				},
				{
					stopId: endLocation.id,
					locationName: endLocation.name,
					coordinates: endLocation.coordinates
				},
				instant,
				searchType
			)
				.then(response => {
					if (!response.ok) throw new Error(JSON.stringify(response))
					return response.responseBody.trips.map(ensureUtc)
				})
				.then(trips => {
					return Promise.all(trips.map(trip => fetchAndAddTripProducts(context, trip, passengers)))
				})
				.then(trips => {
					return Promise.all(trips.map(addSeatingToLegs))
				})
				.then(trips => {
					if (prepend) {
						context.commit('setTrips', [...trips, ...context.state.trips])
					} else if (append) {
						context.commit('setTrips', [...context.state.trips, ...trips])
					} else {
						context.commit('setTrips', trips)
					}
					// if (trip) {
					// 	context.commit('setTrip', trip)
					// }

					return trips
				})
				.finally(() => {
					context.commit('setLoading', false)
				})
		}
	}
}
